import { Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { createPortal } from "react-dom";

export function GuestCounter({
  label,
  subtitle,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  subtitle: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex flex-col">
        <span className="text-[13px] font-semibold text-neutral-800 dark:text-white">
          {label}
        </span>
        <span className="text-[11px] text-neutral-400 mt-0.5">{subtitle}</span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border border-neutral-200 dark:border-zinc-600 flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:border-[var(--brand)] hover:text-[var(--brand)] disabled:opacity-30 transition-all text-base leading-none"
        >
          <Minus />
        </button>
        <span className="text-[14px] font-bold w-5 text-center text-neutral-800 dark:text-white">
          {value}
        </span>
        <button
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-full border border-neutral-200 dark:border-zinc-600 flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-all text-base leading-none"
        >
          <Plus />
        </button>
      </div>
    </div>
  );
}

export function DatePickerPortal({
  anchorRef,
  isRTL,
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  placeholderCheckin,
  placeholderCheckout,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  isRTL: boolean;
  startDate: Date | null;
  endDate: Date | null;
  onChangeStart: (d: Date | null) => void;
  onChangeEnd: (d: Date | null) => void;
  placeholderCheckin: string;
  placeholderCheckout: string;
}) {
  const [style, setStyle] = useState<React.CSSProperties>({ opacity: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setStyle({
      position: "fixed",
      top: rect.top,
      left: isRTL ? undefined : rect.left,
      right: isRTL ? window.innerWidth - rect.right : undefined,
      width: rect.width,
      height: rect.height,
      zIndex: 9999,
      opacity: 1,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      pointerEvents: "auto",
    });
  }, [anchorRef, isRTL]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div style={style}>
      <DatePicker
        selected={startDate}
        onChange={onChangeStart}
        selectsStart
        startDate={startDate ?? undefined}
        endDate={endDate ?? undefined}
        minDate={new Date()}
        placeholderText={placeholderCheckin}
        dateFormat="d MMM yyyy"
        locale={isRTL ? "ar" : "en"}
        popperPlacement={isRTL ? "bottom-end" : "bottom-start"}
        popperProps={{ strategy: "fixed" }}
        className="bg-transparent text-[13px] font-medium text-neutral-800 dark:text-white outline-none cursor-pointer w-full placeholder:text-neutral-500 placeholder:font-normal"
      />
      <span className="text-neutral-300 dark:text-zinc-600 shrink-0">—</span>
      <DatePicker
        selected={endDate}
        onChange={onChangeEnd}
        selectsEnd
        startDate={startDate ?? undefined}
        endDate={endDate ?? undefined}
        minDate={startDate ?? new Date()}
        placeholderText={placeholderCheckout}
        dateFormat="d MMM yyyy"
        locale={isRTL ? "ar" : "en"}
        popperPlacement={isRTL ? "bottom-end" : "bottom-start"}
        popperProps={{ strategy: "fixed" }}
        className="bg-transparent text-[13px] font-medium text-neutral-800 dark:text-white outline-none cursor-pointer w-full placeholder:text-neutral-500 placeholder:font-normal"
      />
    </div>,
    document.body,
  );
}

export function PortalDropdown({
  anchorRef,
  isRTL,
  children,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  isRTL: boolean;
  children: React.ReactNode;
}) {
  const [style, setStyle] = useState<React.CSSProperties>({ opacity: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setStyle({
      position: "fixed",
      top: rect.bottom + 6,
      left: isRTL ? undefined : rect.left,
      right: isRTL ? window.innerWidth - rect.right : undefined,
      width: Math.max(rect.width, 280),
      zIndex: 9999,
      opacity: 1,
    });
  }, [anchorRef, isRTL]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  if (typeof document === "undefined") return null;
  return createPortal(
    // stopPropagation on mousedown so clicks inside the portal (e.g. +/- buttons)
    // don't reach the document outside-click listener that closes the dropdown.
    <div
      style={style}
      className="pointer-events-auto"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

export function nightsBetween(start: Date | null, end: Date | null): number {
  if (!start || !end) return 0;
  const diff = end.getTime() - start.getTime();
  return Math.max(0, Math.round(diff / 86400000));
}
